import React from "react";
import { Box, Paper, TextField, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import useResetPassword from "../../hooks/api/useResetPassword";
import { ResetPasswordFormData } from "./types/forgotPasswordTypes";

interface ResetPasswordFormProps {
  handleSuccess: () => void;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  handleSuccess
}) => {
  const { t } = useKivunovaTranslation();

  const resetPasswordSchema = yup.object({
    otp: yup
      .string()
      .required(t("auth-reset-password-otp-required", "OTP is required"))
      .matches(
        /^\d{6}$/,
        t("auth-reset-password-otp-6digits", "OTP must be 6 digits")
      ),

    newPassword: yup
      .string()
      .min(
        8,
        t(
          "auth-reset-password-min-length",
          "Password must be at least 8 characters"
        )
      )
      .required(
        t("auth-reset-password-new-required", "New password is required")
      ),

    confirmPassword: yup
      .string()
      .oneOf(
        [yup.ref("newPassword")],
        t("auth-reset-password-match", "Passwords must match")
      )
      .required(
        t(
          "auth-reset-password-confirm-required",
          "Confirm password is required"
        )
      )
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm<ResetPasswordFormData>({
    resolver: yupResolver(resetPasswordSchema),
    defaultValues: { otp: "", newPassword: "", confirmPassword: "" },
    mode: "onChange"
  });

  // Watch newPassword for confirm password validation
  watch("newPassword");

  const resetMutation = useResetPassword();
  const isLoading = resetMutation.isPending;

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (isLoading) return;

    try {
      await resetMutation.mutateAsync({
        otp: data.otp,
        newPassword: data.newPassword
      });

      // Handle post-success logic, e.g., navigate or show a message
      handleSuccess();
    } catch (error) {
      // No need for alerts here; the hook handles them
      console.error("Reset password failed:", error);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        p: 6,
        position: "relative",
        width: "100%"
      }}
    >
      <Typography
        textAlign="center"
        variant="h5"
        component="h1"
        gutterBottom
        align="center"
        color="black"
      >
        {t("auth-reset-password-title", "Reset Password")}
      </Typography>

      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 2,
          gap: 3,
          flexDirection: "column",
          display: "flex"
        }}
        component="form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        data-testid="reset-password-form"
      >
        <Controller
          name="otp"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t("auth-reset-password-otp", "OTP")}
              type="text"
              fullWidth
              error={!!errors.otp}
              helperText={errors.otp?.message}
              inputProps={{
                "data-testid": "reset-password-otp",
                "aria-label": t("auth-reset-password-otp-aria", "OTP code")
              }}
            />
          )}
        />

        <Controller
          name="newPassword"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t("auth-reset-password-new-password", "New Password")}
              type="password"
              fullWidth
              error={!!errors.newPassword}
              helperText={errors.newPassword?.message}
              inputProps={{
                "data-testid": "reset-password-new-password",
                "aria-label": t(
                  "auth-reset-password-new-password-aria",
                  "New password"
                )
              }}
            />
          )}
        />

        <Controller
          name="confirmPassword"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t(
                "auth-reset-password-confirm-password",
                "Confirm Password"
              )}
              type="password"
              fullWidth
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              inputProps={{
                "data-testid": "reset-password-confirm-password",
                "aria-label": t(
                  "auth-reset-password-confirm-password-aria",
                  "Confirm password"
                )
              }}
            />
          )}
        />
        <LoadingButton
          type="submit"
          variant="contained"
          fullWidth
          loading={isLoading}
          sx={{
            mt: 4,
            backgroundColor: BRAND_COLOR,
            color: "#fff",
            "&:hover": { backgroundColor: BRAND_HOVER },
            "&:disabled": { backgroundColor: "#ccc", color: "#666" }
          }}
          data-testid="reset-password-submit"
          aria-label={t("auth-reset-password-submit-aria", "Update password")}
        >
          {t("auth-reset-password-submit", "Update Password")}
        </LoadingButton>
      </Box>
    </Paper>
  );
};

export default ResetPasswordForm;
